/** QR 질문·응답 다운로드 — PDF는 과제 다운로드와 동일한 캔버스 캡처 방식, 표는 ExcelJS로 서식(배경색·병합·정렬) 적용 */
import ExcelJS from 'exceljs';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import type { ClassMemberRow } from './classMembersStorage';
import { displayClassMemberName } from './classMemberNameDisplay';
import type { PresentationCommunicationResponse } from '../services/presentationCommunicationService';
import {
  COMMUNICATION_CHOICE_COLORS,
  communicationChoiceOptionLabel,
  evaluateCommunicationQuestionCorrectness,
  isChoiceLikeCommunicationQuestion,
  parseCommunicationChoiceSelection,
  type CommunicationQuestion,
} from './presentationCommunicationAnswers';

export type CommunicationExportChoiceBadge = {
  index: number;
  label: string;
  color: string;
};

/** 멤버 한 명이 제출한 응답 하나(복수 응답 모드면 같은 멤버가 여러 개 가질 수 있음) */
export type CommunicationExportAnswer = {
  /** 사람이 읽는 응답 텍스트(선택형은 "1번, 3번" 형태) */
  responseText: string;
  /** 선택형일 때만 채워짐 — PDF에 색상 번호 배지로 표시 */
  choiceBadges: CommunicationExportChoiceBadge[] | null;
  isCorrect: boolean | null;
  submittedAtLabel: string;
};

export type CommunicationExportRow = {
  studentNumber: string;
  name: string;
  /** 제출 순서대로 — 복수 응답이면 2개 이상 */
  answers: CommunicationExportAnswer[];
};

export type CommunicationExportSection = {
  promptText: string;
  correctAnswerText: string;
  correctAnswerBadges: CommunicationExportChoiceBadge[] | null;
  rows: CommunicationExportRow[];
};

/** 문서 최상위에 표시할 프레젠테이션 정보(편제명/단원명/쪽수/학습내용) */
export type CommunicationExportDocumentMeta = {
  track: string;
  unit: string;
  page: string;
  programContent: string;
};

export type CommunicationExportPayload = {
  /** 질문 순서대로 — PDF는 섹션마다 새 페이지에서 시작 */
  sections: CommunicationExportSection[];
  /** 확장자 제외 파일명(이미 특수문자 제거된 값 권장) */
  fileBaseName: string;
  documentMeta?: CommunicationExportDocumentMeta;
};

function hasDocumentMetaText(meta: CommunicationExportDocumentMeta | undefined): meta is CommunicationExportDocumentMeta {
  if (!meta) return false;
  return Boolean(
    (meta.track || '').trim() ||
      (meta.unit || '').trim() ||
      (meta.page || '').trim() ||
      (meta.programContent || '').trim()
  );
}

function responseTimeMs(updatedAt: string | undefined): number {
  if (!updatedAt) return 0;
  const t = Date.parse(updatedAt);
  return Number.isFinite(t) ? t : 0;
}

function groupResponsesByMemberId(
  responses: PresentationCommunicationResponse[]
): Map<string, PresentationCommunicationResponse[]> {
  const map = new Map<string, PresentationCommunicationResponse[]>();
  for (const r of responses) {
    const list = map.get(r.memberId);
    if (list) list.push(r);
    else map.set(r.memberId, [r]);
  }
  Array.from(map.values()).forEach((list) => {
    list.sort(
      (a: PresentationCommunicationResponse, b: PresentationCommunicationResponse) =>
        responseTimeMs(a.updatedAt) - responseTimeMs(b.updatedAt)
    );
  });
  return map;
}

function formatCommunicationCorrectAnswer(
  question: CommunicationQuestion | null,
  legacyAnswerText: string
): string {
  if (isChoiceLikeCommunicationQuestion(question)) {
    const choices = question?.choices ?? [];
    const idxs = question?.correctChoiceIndexes ?? [];
    if (idxs.length === 0) return '';
    return idxs
      .map((i) => communicationChoiceOptionLabel(choices[i], i))
      .filter(Boolean)
      .join(', ');
  }
  if (question?.type === 'short') {
    return (question.answers ?? []).map((a) => a.trim()).filter(Boolean).join(', ');
  }
  if (question?.type === 'essay') return '';
  return String(legacyAnswerText ?? '')
    .split('#')
    .map((p) => p.trim())
    .filter(Boolean)
    .join(', ');
}

/** 질문 하나 + 그 응답들로 CSV/PDF에 넘길 섹션 데이터를 만든다. */
export function buildCommunicationExportSection(params: {
  promptText: string;
  answerText: string;
  question: CommunicationQuestion | null;
  /** 번호 매기기 기준 순서(그룹 순서로 이어붙인 전체 멤버 목록) */
  members: ClassMemberRow[];
  memberNumberById: Map<string, number>;
  responses: PresentationCommunicationResponse[];
}): CommunicationExportSection {
  const { promptText, answerText, question, members, memberNumberById, responses } = params;
  const choices = question?.choices ?? [];
  const isChoice = isChoiceLikeCommunicationQuestion(question);
  const responsesByMemberId = groupResponsesByMemberId(responses);

  const buildBadges = (indexes: number[]): CommunicationExportChoiceBadge[] =>
    indexes.map((i) => ({
      index: i,
      label: communicationChoiceOptionLabel(choices[i], i),
      color: COMMUNICATION_CHOICE_COLORS[i % COMMUNICATION_CHOICE_COLORS.length]!,
    }));

  const correctIdxs = isChoice
    ? [...(question?.correctChoiceIndexes ?? [])]
        .filter((i) => Number.isFinite(i) && i >= 0)
        .sort((a, b) => a - b)
    : [];

  const rows: CommunicationExportRow[] = [];
  for (const member of members) {
    const memberResponses = responsesByMemberId.get(member.id) ?? [];
    if (memberResponses.length === 0) continue;
    const num = memberNumberById.get(member.id) ?? 0;
    const studentNumber = String(num).padStart(2, '0');

    const answers: CommunicationExportAnswer[] = memberResponses.map((resp) => {
      const body = (resp.bodyText ?? '').trim();
      const correctness = evaluateCommunicationQuestionCorrectness(body, question ?? null, answerText);
      const selectedIdxs = isChoice ? parseCommunicationChoiceSelection(body, choices) : [];
      const responseText = isChoice
        ? selectedIdxs.map((i) => communicationChoiceOptionLabel(choices[i], i)).join(', ')
        : body;

      let submittedAtLabel = '';
      const t = Date.parse(resp.updatedAt ?? '');
      if (Number.isFinite(t)) {
        submittedAtLabel = new Date(t).toLocaleString('ko-KR', {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      return {
        responseText: responseText || '',
        choiceBadges: isChoice && selectedIdxs.length > 0 ? buildBadges(selectedIdxs) : null,
        isCorrect: correctness,
        submittedAtLabel,
      };
    });

    rows.push({ studentNumber, name: displayClassMemberName(member).trim() || '이름 없음', answers });
  }

  return {
    promptText: (promptText || '').trim(),
    correctAnswerText: formatCommunicationCorrectAnswer(question, answerText),
    correctAnswerBadges: isChoice && correctIdxs.length > 0 ? buildBadges(correctIdxs) : null,
    rows,
  };
}

function correctnessLabel(isCorrect: boolean | null): string {
  if (isCorrect === true) return '정답';
  if (isCorrect === false) return '오답';
  return '-';
}

/* ----------------------------- Excel(.xlsx) 다운로드 ----------------------------- */
/**
 * 질문/정답/응답표를 배경색·병합·정렬이 적용된 엑셀 파일로 내려준다.
 * (일반 CSV는 셀 배경색·병합·정렬·글자 크기 같은 서식을 담을 수 없어 xlsx로 만든다.)
 */
const XLSX_COLUMN_COUNT = 5; // A:번호 B:이름 C:응답 D:정답여부 E:제출시각
const XLSX_DARK_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBDBDBD' } };
const XLSX_LIGHT_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
const XLSX_WHITE_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
const XLSX_BASE_FONT_SIZE = 12;
const XLSX_TITLE_FONT_SIZE = 13;
const XLSX_BULLET_FONT_SIZE = 14;
/** PDF 응답 불릿과 동일 색상 */
const XLSX_BULLET_CORRECT_ARGB = 'FF10504A';
const XLSX_BULLET_INCORRECT_ARGB = 'FFFF6B6B';
const XLSX_BULLET_NEUTRAL_ARGB = 'FF9CA3AF';
const XLSX_RESPONSE_BULLET = '●';

function xlsxBorder(): Partial<ExcelJS.Borders> {
  const side: ExcelJS.Border = { style: 'thin', color: { argb: 'FFB0B0B0' } };
  return { top: { ...side }, left: { ...side }, bottom: { ...side }, right: { ...side } };
}

function bulletArgbForCorrectness(isCorrect: boolean | null): string {
  if (isCorrect === true) return XLSX_BULLET_CORRECT_ARGB;
  if (isCorrect === false) return XLSX_BULLET_INCORRECT_ARGB;
  return XLSX_BULLET_NEUTRAL_ARGB;
}

/** 정답여부·제출시각 등: 복수면 줄바꿈만 (응답열의 색 원형 기호와 줄 수를 맞춤) */
function joinAnswerLines(lines: string[]): string {
  return lines.join('\n');
}

/** 응답 셀: 모든 응답 앞에 PDF와 같은 색의 원형(●)을 붙인 richText */
function buildResponseRichText(answers: CommunicationExportAnswer[]): ExcelJS.CellValue {
  const richText: ExcelJS.RichText[] = [];
  answers.forEach((answer, idx) => {
    if (idx > 0) {
      richText.push({ font: { size: XLSX_BASE_FONT_SIZE }, text: '\n' });
    }
    richText.push({
      font: {
        size: XLSX_BULLET_FONT_SIZE,
        color: { argb: bulletArgbForCorrectness(answer.isCorrect) },
        bold: true,
      },
      text: `${XLSX_RESPONSE_BULLET} `,
    });
    richText.push({
      font: { size: XLSX_BASE_FONT_SIZE, color: { argb: 'FF111827' } },
      text: answer.responseText || '(응답 없음)',
    });
  });
  if (richText.length === 0) {
    return '';
  }
  return { richText };
}

function writeSectionLabelRow(
  sheet: ExcelJS.Worksheet,
  rowNumber: number,
  label: string,
  text: string,
  fill: ExcelJS.Fill
): void {
  sheet.mergeCells(rowNumber, 2, rowNumber, XLSX_COLUMN_COUNT);
  const row = sheet.getRow(rowNumber);

  const labelCell = row.getCell(1);
  labelCell.value = label;
  labelCell.font = { bold: true, size: XLSX_TITLE_FONT_SIZE };
  labelCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  labelCell.fill = fill;
  labelCell.border = xlsxBorder();

  const textCell = row.getCell(2);
  textCell.value = text;
  textCell.font = { size: XLSX_BASE_FONT_SIZE };
  textCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
  textCell.fill = fill;
  for (let col = 2; col <= XLSX_COLUMN_COUNT; col += 1) {
    const cell = row.getCell(col);
    cell.fill = fill;
    cell.border = xlsxBorder();
  }
}

/** 질문 사이를 표가 나뉘어 보이도록 테두리 없는 흰 배경 한 줄을 넣는다. */
function writeBlankSeparatorRow(sheet: ExcelJS.Worksheet, rowNumber: number): void {
  const row = sheet.getRow(rowNumber);
  row.height = 12;
  for (let col = 1; col <= XLSX_COLUMN_COUNT; col += 1) {
    row.getCell(col).fill = XLSX_WHITE_FILL;
  }
}

/** 질문 하나(질문/정답/헤더/응답 데이터)를 시트에 쓰고, 다음에 쓸 행 번호를 반환한다. */
function writeSectionToSheet(
  sheet: ExcelJS.Worksheet,
  section: CommunicationExportSection,
  sectionIndex: number,
  startRow: number
): number {
  let rowNumber = startRow;

  writeSectionLabelRow(sheet, rowNumber, `질문 ${sectionIndex + 1}`, section.promptText || '(질문 없음)', XLSX_DARK_FILL);
  rowNumber += 1;

  if (section.correctAnswerText) {
    writeSectionLabelRow(sheet, rowNumber, '정답', section.correctAnswerText, XLSX_WHITE_FILL);
    rowNumber += 1;
  }

  const headerRow = sheet.getRow(rowNumber);
  ['번호', '이름', '응답', '정답여부', '제출시각'].forEach((label, colIndex) => {
    const cell = headerRow.getCell(colIndex + 1);
    cell.value = label;
    cell.font = { bold: true, size: XLSX_TITLE_FONT_SIZE };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.fill = XLSX_LIGHT_FILL;
    cell.border = xlsxBorder();
  });
  rowNumber += 1;

  if (section.rows.length === 0) {
    sheet.mergeCells(rowNumber, 2, rowNumber, XLSX_COLUMN_COUNT);
    const row = sheet.getRow(rowNumber);
    for (let col = 1; col <= XLSX_COLUMN_COUNT; col += 1) {
      const cell = row.getCell(col);
      cell.fill = XLSX_WHITE_FILL;
      cell.border = xlsxBorder();
    }
    const cell = row.getCell(1);
    cell.value = '(제출된 응답 없음)';
    cell.font = { size: XLSX_BASE_FONT_SIZE, color: { argb: 'FF9CA3AF' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    rowNumber += 1;
  } else {
    for (const dataRow of section.rows) {
      const correctnessText = joinAnswerLines(dataRow.answers.map((a) => correctnessLabel(a.isCorrect)));
      const submittedText = joinAnswerLines(dataRow.answers.map((a) => a.submittedAtLabel));

      const row = sheet.getRow(rowNumber);

      const numCell = row.getCell(1);
      numCell.value = dataRow.studentNumber;
      const nameCell = row.getCell(2);
      nameCell.value = dataRow.name;
      const respCell = row.getCell(3);
      respCell.value = buildResponseRichText(dataRow.answers);
      const correctCell = row.getCell(4);
      correctCell.value = correctnessText;
      const timeCell = row.getCell(5);
      timeCell.value = submittedText;

      [numCell, nameCell, correctCell, timeCell].forEach((cell) => {
        cell.font = { size: XLSX_BASE_FONT_SIZE };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.fill = XLSX_WHITE_FILL;
        cell.border = xlsxBorder();
      });
      respCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      respCell.fill = XLSX_WHITE_FILL;
      respCell.border = xlsxBorder();

      rowNumber += 1;
    }
  }

  return rowNumber;
}

function triggerXlsxDownload(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** 문서 최상위 타이틀(편제명·단원명·쪽수 / 학습내용)을 크게 표시한다. */
function writeDocumentTitleRows(sheet: ExcelJS.Worksheet, meta: CommunicationExportDocumentMeta, startRow: number): number {
  let rowNumber = startRow;
  const metaLine = [meta.track, meta.unit, meta.page].map((s) => (s || '').trim()).filter(Boolean).join('  ·  ');
  const programLine = (meta.programContent || '').trim();

  if (metaLine) {
    sheet.mergeCells(rowNumber, 1, rowNumber, XLSX_COLUMN_COUNT);
    const cell = sheet.getCell(rowNumber, 1);
    cell.value = metaLine;
    cell.font = { bold: true, size: 18 };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    rowNumber += 1;
  }
  if (programLine) {
    sheet.mergeCells(rowNumber, 1, rowNumber, XLSX_COLUMN_COUNT);
    const cell = sheet.getCell(rowNumber, 1);
    cell.value = programLine;
    cell.font = { size: 14, color: { argb: 'FF374151' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    rowNumber += 1;
  }
  if (metaLine || programLine) {
    writeBlankSeparatorRow(sheet, rowNumber);
    rowNumber += 1;
  }
  return rowNumber;
}

/** 질문·응답 전체를 배경색·병합·정렬이 적용된 엑셀(.xlsx)로 만들어 바로 내려받는다. */
export async function downloadCommunicationResponsesExcel(payload: CommunicationExportPayload): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('질문응답');
  sheet.columns = [{ width: 8 }, { width: 14 }, { width: 56 }, { width: 12 }, { width: 22 }];
  sheet.pageSetup = {
    paperSize: 9, // A4
    orientation: 'portrait',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0, footer: 0 },
  };

  let rowNumber = 1;
  if (hasDocumentMetaText(payload.documentMeta)) {
    rowNumber = writeDocumentTitleRows(sheet, payload.documentMeta, rowNumber);
  }
  payload.sections.forEach((section, idx) => {
    if (idx > 0) {
      writeBlankSeparatorRow(sheet, rowNumber);
      rowNumber += 1;
    }
    rowNumber = writeSectionToSheet(sheet, section, idx, rowNumber);
  });

  if (payload.sections.length === 0) {
    sheet.getCell(1, 1).value = '질문이 없습니다.';
    sheet.getCell(1, 1).font = { size: XLSX_BASE_FONT_SIZE };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  triggerXlsxDownload(buffer, payload.fileBaseName);
}

/* ----------------------------- PDF 다운로드 ----------------------------- */

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PAGE_MARGIN_MM = 12;
const GRID_COLUMNS = 5;
const GRID_GAP_PX = 8;

function mmToPx(mm: number): number {
  return Math.round((mm * 96) / 25.4);
}

const A4_WIDTH_PX = mmToPx(A4_WIDTH_MM);
const PAGE_MARGIN_PX = mmToPx(PAGE_MARGIN_MM);
const CONTENT_WIDTH_PX = A4_WIDTH_PX - PAGE_MARGIN_PX * 2;
const PAGE_CONTENT_HEIGHT_PX = mmToPx(A4_HEIGHT_MM - PAGE_MARGIN_MM * 2);
const GRID_COL_WIDTH_PX = Math.floor(
  (CONTENT_WIDTH_PX - GRID_GAP_PX * (GRID_COLUMNS - 1)) / GRID_COLUMNS
);

function buildChoiceBadgesEl(badges: CommunicationExportChoiceBadge[]): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;';
  for (const badge of badges) {
    const span = document.createElement('span');
    span.style.cssText = `
      display:inline-flex;align-items:center;justify-content:center;
      min-width:16px;height:16px;padding:0 3px;border-radius:5px;
      background:${badge.color};color:#111;font-size:10px;font-weight:700;
      border:1px solid rgba(0,0,0,0.08);box-sizing:border-box;
    `;
    span.textContent = String(badge.index + 1);
    wrap.appendChild(span);
  }
  return wrap;
}

function buildHeaderEl(section: CommunicationExportSection, sectionIndex: number): HTMLElement {
  const header = document.createElement('div');
  header.style.cssText = `
    box-sizing:border-box;width:${CONTENT_WIDTH_PX}px;
    padding-bottom:16px;margin-bottom:16px;border-bottom:2px solid #1f2937;
    font-family:'Apple SD Gothic Neo','Malgun Gothic',sans-serif;color:#111827;
  `;

  const label = document.createElement('div');
  label.style.cssText = 'font-size:12px;font-weight:700;color:#6b7280;letter-spacing:.04em;margin-bottom:6px;';
  label.textContent = `질문 ${sectionIndex + 1}`;
  header.appendChild(label);

  const prompt = document.createElement('div');
  prompt.style.cssText = 'font-size:19px;font-weight:700;line-height:1.5;white-space:pre-wrap;word-break:break-word;';
  prompt.textContent = section.promptText || '(질문 없음)';
  header.appendChild(prompt);

  if (section.correctAnswerText || (section.correctAnswerBadges && section.correctAnswerBadges.length > 0)) {
    const ansWrap = document.createElement('div');
    ansWrap.style.cssText = 'margin-top:12px;padding-top:10px;border-top:1px dashed #d1d5db;';
    const ansLabel = document.createElement('div');
    ansLabel.style.cssText = 'font-size:12px;font-weight:700;color:#10504a;letter-spacing:.04em;margin-bottom:6px;';
    ansLabel.textContent = '정답';
    ansWrap.appendChild(ansLabel);
    if (section.correctAnswerBadges && section.correctAnswerBadges.length > 0) {
      ansWrap.appendChild(buildChoiceBadgesEl(section.correctAnswerBadges));
    } else {
      const ansText = document.createElement('div');
      ansText.style.cssText = 'font-size:14px;font-weight:600;color:#10504a;white-space:pre-wrap;word-break:break-word;';
      ansText.textContent = section.correctAnswerText;
      ansWrap.appendChild(ansText);
    }
    header.appendChild(ansWrap);
  }

  return header;
}

/** 문서 최상위 타이틀(편제명·단원명·쪽수 / 학습내용) — PDF는 모든 쪽에 머릿글로 반복 표시한다. */
function buildDocumentTitleEl(meta: CommunicationExportDocumentMeta): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.cssText = `
    box-sizing:border-box;width:${CONTENT_WIDTH_PX}px;
    padding-bottom:10px;margin-bottom:14px;border-bottom:1.5px solid #9ca3af;
    font-family:'Apple SD Gothic Neo','Malgun Gothic',sans-serif;color:#111827;
  `;

  const metaLine = [meta.track, meta.unit, meta.page].map((s) => (s || '').trim()).filter(Boolean).join('  ·  ');
  if (metaLine) {
    const line1 = document.createElement('div');
    line1.style.cssText = 'font-size:17px;font-weight:800;letter-spacing:-0.01em;';
    line1.textContent = metaLine;
    wrap.appendChild(line1);
  }

  const programLine = (meta.programContent || '').trim();
  if (programLine) {
    const line2 = document.createElement('div');
    line2.style.cssText = `font-size:13px;font-weight:600;color:#4b5563;white-space:pre-wrap;word-break:break-word;${
      metaLine ? 'margin-top:4px;' : ''
    }`;
    line2.textContent = programLine;
    wrap.appendChild(line2);
  }

  return wrap;
}

function bulletColorForAnswer(isCorrect: boolean | null): string {
  if (isCorrect === true) return '#10504a';
  if (isCorrect === false) return '#ff6b6b';
  return '#9ca3af';
}

/** 응답 하나(불릿 + 내용 + 제출시각)를 나타내는 줄. 복수 응답이면 이 줄이 여러 개 쌓인다. */
function buildAnswerLineEl(answer: CommunicationExportAnswer, isFirst: boolean): HTMLElement {
  const line = document.createElement('div');
  line.style.cssText = `display:flex;align-items:flex-start;gap:5px;${isFirst ? '' : 'margin-top:5px;'}`;

  const dot = document.createElement('span');
  dot.style.cssText = `
    flex:0 0 auto;width:6px;height:6px;border-radius:50%;margin-top:4px;
    background:${bulletColorForAnswer(answer.isCorrect)};
  `;
  line.appendChild(dot);

  const body = document.createElement('div');
  body.style.cssText = 'flex:1 1 auto;min-width:0;';

  if (answer.choiceBadges && answer.choiceBadges.length > 0) {
    body.appendChild(buildChoiceBadgesEl(answer.choiceBadges));
  } else {
    const textEl = document.createElement('div');
    textEl.style.cssText = 'font-size:10.5px;line-height:1.4;white-space:pre-wrap;word-break:break-word;';
    textEl.textContent = answer.responseText || '응답 없음';
    body.appendChild(textEl);
  }

  if (answer.submittedAtLabel) {
    const timeEl = document.createElement('div');
    timeEl.style.cssText = 'margin-top:2px;font-size:8px;color:#9ca3af;';
    timeEl.textContent = answer.submittedAtLabel;
    body.appendChild(timeEl);
  }

  line.appendChild(body);
  return line;
}

function buildRowBlockEl(row: CommunicationExportRow): HTMLElement {
  const lastAnswer = row.answers[row.answers.length - 1] ?? null;
  const isCorrect = lastAnswer?.isCorrect ?? null;
  const borderColor = isCorrect === true ? '#10504a' : isCorrect === false ? '#ff6b6b' : '#e5e7eb';
  const block = document.createElement('div');
  block.style.cssText = `
    position:relative;box-sizing:border-box;width:${GRID_COL_WIDTH_PX}px;flex:0 0 ${GRID_COL_WIDTH_PX}px;
    border:1.3px solid ${borderColor};border-radius:9px;padding:7px 7px 6px;
    font-family:'Apple SD Gothic Neo','Malgun Gothic',sans-serif;color:#111827;
    background:#ffffff;
  `;

  if (isCorrect !== null) {
    const ok = isCorrect === true;
    const badge = document.createElement('span');
    badge.style.cssText = `
      position:absolute;top:-7px;right:-7px;width:15px;height:15px;border-radius:50%;
      display:inline-flex;align-items:center;justify-content:center;
      background:${ok ? '#10504a' : '#ff6b6b'};color:#fff;font-size:9px;font-weight:800;line-height:1;
    `;
    badge.textContent = ok ? 'O' : 'X';
    block.appendChild(badge);
  }

  const head = document.createElement('div');
  head.style.cssText = 'display:flex;align-items:center;gap:4px;margin-bottom:5px;';

  const numEl = document.createElement('span');
  numEl.style.cssText = `
    display:inline-flex;align-items:center;justify-content:center;flex:0 0 auto;
    min-width:18px;height:15px;padding:0 3px;border-radius:5px;
    background:#1f2937;color:#fff;font-size:9px;font-weight:700;
  `;
  numEl.textContent = row.studentNumber;
  head.appendChild(numEl);

  const nameEl = document.createElement('span');
  nameEl.style.cssText =
    'font-size:11px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;flex:1 1 auto;';
  nameEl.textContent = row.name;
  head.appendChild(nameEl);

  block.appendChild(head);

  row.answers.forEach((answer, idx) => {
    block.appendChild(buildAnswerLineEl(answer, idx === 0));
  });

  return block;
}

function buildEmptyNoticeEl(): HTMLElement {
  const notice = document.createElement('div');
  notice.style.cssText = 'font-size:14px;color:#9ca3af;padding:20px 0;';
  notice.textContent = '아직 제출된 응답이 없습니다.';
  return notice;
}

async function captureElementToCanvas(el: HTMLElement): Promise<HTMLCanvasElement> {
  return html2canvas(el, {
    scale: 2,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
  });
}

function addCanvasAsPdfPage(pdf: jsPDF, canvas: HTMLCanvasElement, isFirstPage: boolean): void {
  if (!isFirstPage) pdf.addPage();
  const pageW = pdf.internal.pageSize.getWidth();
  const marginMm = PAGE_MARGIN_MM;
  const drawW = pageW - marginMm * 2;
  const drawH = (canvas.height * drawW) / canvas.width;
  const imgData = canvas.toDataURL('image/png');
  pdf.addImage(imgData, 'PNG', marginMm, marginMm, drawW, drawH);
}

/**
 * 응답 블록을 5열 그리드 행으로 묶어 페이지에 채워 넣기 위한 단위.
 * 한 행 안에서는 align-items:stretch로, 텍스트가 많아 블록이 높아지면
 * 같은 행의 다른 블록도 자동으로 같은 높이로 늘어난다.
 */
function buildGridRowEls(rows: CommunicationExportRow[]): HTMLElement[] {
  const rowEls: HTMLElement[] = [];
  for (let i = 0; i < rows.length; i += GRID_COLUMNS) {
    const chunk = rows.slice(i, i + GRID_COLUMNS);
    const rowContainer = document.createElement('div');
    rowContainer.style.cssText = `
      display:flex;align-items:stretch;gap:${GRID_GAP_PX}px;
      width:${CONTENT_WIDTH_PX}px;margin-bottom:${GRID_GAP_PX}px;
    `;
    for (const row of chunk) {
      rowContainer.appendChild(buildRowBlockEl(row));
    }
    rowEls.push(rowContainer);
  }
  return rowEls;
}

/**
 * A4 세로 PDF: 질문마다 새 페이지에서 시작. 각 질문은 상단에 질문(+정답),
 * 하단에 응답을 번호순 5열 그리드 블록으로 배치(필요 시 같은 질문도 여러 장으로 자동 분할).
 * 복수 응답은 같은 멤버의 응답을 한 블록에 불릿(●)으로 줄바꿈해 모아 보여준다.
 */
export async function savePresentationCommunicationResponsesAsPdf(
  payload: CommunicationExportPayload
): Promise<void> {
  const measureRoot = document.createElement('div');
  measureRoot.style.cssText = `
    position:fixed;left:-10000px;top:0;z-index:-1;
    width:${A4_WIDTH_PX}px;background:#ffffff;
  `;
  document.body.appendChild(measureRoot);

  try {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    let isFirstPdfPage = true;

    /** 편제명·단원명·쪽수/학습내용 — 있으면 모든 쪽 맨 위에 머릿글로 반복 표시 */
    let documentTitleEl: HTMLElement | null = null;
    let documentTitleHeight = 0;
    if (hasDocumentMetaText(payload.documentMeta)) {
      documentTitleEl = buildDocumentTitleEl(payload.documentMeta);
      measureRoot.appendChild(documentTitleEl);
      documentTitleHeight = documentTitleEl.getBoundingClientRect().height;
      measureRoot.removeChild(documentTitleEl);
    }
    const pageContentHeightPx = PAGE_CONTENT_HEIGHT_PX - documentTitleHeight;

    const prependDocumentTitle = (container: HTMLElement): void => {
      if (documentTitleEl) {
        container.appendChild(documentTitleEl.cloneNode(true) as HTMLElement);
      }
    };

    const sections = payload.sections.length > 0 ? payload.sections : [];
    if (sections.length === 0) {
      const pageDiv = document.createElement('div');
      pageDiv.style.cssText = `width:${CONTENT_WIDTH_PX}px;background:#ffffff;`;
      prependDocumentTitle(pageDiv);
      const notice = document.createElement('div');
      notice.style.cssText = 'font-size:14px;color:#9ca3af;padding:20px 0;';
      notice.textContent = '질문이 없습니다.';
      pageDiv.appendChild(notice);
      measureRoot.appendChild(pageDiv);
      const canvas = await captureElementToCanvas(pageDiv);
      measureRoot.removeChild(pageDiv);
      addCanvasAsPdfPage(pdf, canvas, true);
      pdf.save(`${payload.fileBaseName}.pdf`);
      return;
    }

    for (let sectionIdx = 0; sectionIdx < sections.length; sectionIdx += 1) {
      const section = sections[sectionIdx]!;
      const headerEl = buildHeaderEl(section, sectionIdx);
      measureRoot.appendChild(headerEl);
      const headerHeight = headerEl.getBoundingClientRect().height;
      measureRoot.removeChild(headerEl);

      const rowEls = buildGridRowEls(section.rows);
      rowEls.forEach((el) => measureRoot.appendChild(el));
      const rowHeights = rowEls.map((el) => el.getBoundingClientRect().height);
      rowEls.forEach((el) => measureRoot.removeChild(el));

      const pages: HTMLElement[][] = [];
      let currentPage: HTMLElement[] = [];
      let currentHeight = headerHeight;
      pages.push(currentPage);

      rowEls.forEach((el, idx) => {
        const h = rowHeights[idx] ?? 0;
        if (currentPage.length > 0 && currentHeight + h > pageContentHeightPx) {
          currentPage = [];
          pages.push(currentPage);
          currentHeight = 0;
        }
        currentPage.push(el);
        currentHeight += h;
      });

      for (let pageIdx = 0; pageIdx < pages.length; pageIdx += 1) {
        const pageDiv = document.createElement('div');
        pageDiv.style.cssText = `width:${CONTENT_WIDTH_PX}px;background:#ffffff;`;
        prependDocumentTitle(pageDiv);
        if (pageIdx === 0) {
          pageDiv.appendChild(headerEl);
          if (section.rows.length === 0) {
            pageDiv.appendChild(buildEmptyNoticeEl());
          }
        }
        for (const rowEl of pages[pageIdx]!) {
          pageDiv.appendChild(rowEl);
        }

        measureRoot.appendChild(pageDiv);
        const canvas = await captureElementToCanvas(pageDiv);
        measureRoot.removeChild(pageDiv);

        if (canvas.width < 2 || canvas.height < 2) {
          throw new Error('PDF 캡처에 실패했습니다.');
        }
        addCanvasAsPdfPage(pdf, canvas, isFirstPdfPage);
        isFirstPdfPage = false;
      }
    }

    pdf.save(`${payload.fileBaseName}.pdf`);
  } finally {
    if (measureRoot.parentNode) {
      document.body.removeChild(measureRoot);
    }
  }
}
